import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui';
import { ErrorState } from '@/components/states';
import { Skeleton } from '@/components/ui/Skeleton';
import { SettingsSubPage } from '@/pages/settings/SettingsSubPage';
import { FullScreenSheet } from '@/components/ui/FullScreenSheet';
import { PrivacyPolicyBody } from '@/pages/settings/legal/PrivacyPolicyBody';
import { TermsOfServiceBody } from '@/pages/settings/legal/TermsOfServiceBody';
import { UsageMeter } from '@/components/domain/shared/UsageMeter';
import {
  PlanCard,
  type PlanCardState,
} from '@/components/domain/shared/PlanCard';
import { DowngradeReasonModal } from '@/components/domain/shared/DowngradeReasonModal';
import { householdService } from '@/services';
import * as billingService from '@/services/billing.service';
import { openExternalTab } from '@/lib/externalTab';
import { toast } from '@/stores';
import { useAppShell, useUserRole } from '@/hooks';
import { TIERS } from '@/constants/billing';
import type { SubscriptionWithSchedule, SubscriptionTier } from '@/types';

const TIER_RANK: Record<SubscriptionTier, number> = {
  FREE: 0,
  CORE: 1,
  FAMILY: 2,
};

/**
 * Approved-email usage resets on the SUBSCRIPTION anniversary — one
 * month from `tier_started_at`, clamped for short months — not on the
 * 1st of the calendar month. The boundary is computed server-side and
 * arrives as `period_end` on `v_email_approved_current_period`, the
 * same row the approval gate reads, so the label cannot drift away
 * from the limit it describes.
 *
 * `period_end` is a real instant, so rendering it in the viewer's
 * timezone is the honest thing to do: it is the moment the allowance
 * actually comes back for them, and it matches how the "Your plan
 * cancels on …" line below formats `current_period_end`. Returns undefined when the
 * boundary is unknown, and the meter then omits the line rather than
 * inventing a date.
 *
 * The LOCALE is pinned to en-US, which is a different axis from the
 * timezone: Yaruta ships one language, so a viewer whose browser is set
 * to Spanish must still read "June 25", not "25 de junio". Passing
 * `undefined` follows the browser instead, and QA captured exactly that
 * — "Resets 25 de junio" inside an otherwise English screen (YAR-230).
 */
// Pure helper; exported only so the boundary can be unit-tested.
// eslint-disable-next-line react-refresh/only-export-components
export function quotaResetLabel(periodEnd: string | null): string | undefined {
  if (!periodEnd) return undefined;
  const parsed = new Date(periodEnd);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Formats `subscriptions.current_period_end`, which the Stripe webhook
 * writes from the subscription's unix `current_period_end` — a real
 * instant at an arbitrary time of day, not a civil date. The local
 * calendar day on which that instant falls IS the day the viewer's card
 * is charged, so the viewer's own zone is the correct lens — the same
 * reasoning as `quotaResetLabel` above, locale pin included: this line
 * sits two rows under the reset date and would otherwise switch to
 * Spanish alongside it.
 */
function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// Copy is verbatim from the Figma Plan frame (YAR-230): comma-joined, no
// terminal period, and the warning tier says "avoid interruption" — it is
// the softer nudge BEFORE the limit, so it must not repeat the limit-
// reached sentence.
function statusLineFor(used: number, quota: number | null): string | undefined {
  if (quota === null) return undefined;
  if (used >= quota) return 'Limit reached, upgrade to continue';
  if (used / quota >= 0.75)
    return "You're approaching your limit, upgrade to avoid interruption";
  return undefined;
}

function ctaForTarget(
  current: SubscriptionTier,
  target: SubscriptionTier,
): { label: string; isUpgrade: boolean } | null {
  if (current === target) return null;
  if (TIER_RANK[target] > TIER_RANK[current]) {
    return { label: `Upgrade to ${TIERS[target].label}`, isUpgrade: true };
  }
  return { label: `Switch to ${TIERS[target].label}`, isUpgrade: false };
}

function PlanSkeleton() {
  return (
    <div className="flex flex-col gap-10 px-5 py-5">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    </div>
  );
}

export function SettingsPlan() {
  const { isPrimary, roleResolved } = useUserRole();
  const { householdId, householdResolved } = useAppShell();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [subscription, setSubscription] =
    useState<SubscriptionWithSchedule | null>(null);
  const [reviewed, setReviewed] = useState<number | null>(null);
  const [quotaPeriodEnd, setQuotaPeriodEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actingTier, setActingTier] = useState<SubscriptionTier | null>(null);
  const [managing, setManaging] = useState(false);
  const [downgradeTarget, setDowngradeTarget] =
    useState<SubscriptionTier | null>(null);
  // Legal copy opens over the Plan screen rather than routing away, so a
  // half-finished upgrade isn't lost to a navigation — the same
  // FullScreenSheet pattern Settings → Help & About uses.
  const [legalSheet, setLegalSheet] = useState<'terms' | 'privacy' | null>(
    null,
  );

  /**
   * Armed the moment we hand off to Stripe (Checkout or the Customer
   * Portal). Tells the return-to-tab listener below that this page's state
   * is now potentially stale and its CTAs potentially stuck.
   */
  const handedOffToStripe = useRef(false);
  const refreshing = useRef(false);

  const fetchPlanState = useCallback(async (hid: string) => {
    const [sub, usage] = await Promise.all([
      householdService.getSubscription(hid),
      householdService.getEmailQuotaPeriod(hid),
    ]);
    setSubscription(sub as SubscriptionWithSchedule | null);
    setReviewed(usage.used);
    setQuotaPeriodEnd(usage.periodEnd);
  }, []);

  const loadData = useCallback(
    async (hid: string) => {
      setLoadError(false);
      setLoading(true);
      try {
        await fetchPlanState(hid);
      } catch (err) {
        console.error('Failed to load plan state:', err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    [fetchPlanState],
  );

  /**
   * Background re-read: no skeleton, and a failure leaves the current view
   * alone rather than replacing the whole page with an error state. Used
   * when the user comes back to this tab, where a transient network blip
   * must not blow away a perfectly good page.
   */
  const refreshQuietly = useCallback(
    async (hid: string) => {
      if (refreshing.current) return;
      refreshing.current = true;
      try {
        await fetchPlanState(hid);
      } catch (err) {
        console.warn('Background plan refresh failed (non-blocking):', err);
      } finally {
        refreshing.current = false;
      }
    },
    [fetchPlanState],
  );

  useEffect(() => {
    if (!householdId) {
      // Only stop showing the skeleton once resolution has FINISHED.
      // Otherwise the first paint (id still pending) renders this page's
      // empty/default state before the real data can possibly arrive.
      if (householdResolved) setLoading(false);
      return;
    }
    void loadData(householdId);
  }, [householdId, householdResolved, loadData]);

  // Tier is provisioned asynchronously by the Stripe webhook, so after a
  // successful checkout the row may still read FREE for a beat. Poll
  // briefly until it flips rather than showing a stale plan.
  const pollForActivation = useCallback(
    async (hid: string) => {
      const deadline = Date.now() + 16000;
      while (Date.now() < deadline) {
        try {
          const sub = await householdService.getSubscription(hid);
          if (sub && sub.tier !== 'FREE') {
            setSubscription(sub as SubscriptionWithSchedule);
            // An upgrade bumps tier_started_at, so the window — and the
            // reset label with it — re-anchors on the upgrade date.
            const usage = await householdService.getEmailQuotaPeriod(hid);
            setReviewed(usage.used);
            setQuotaPeriodEnd(usage.periodEnd);
            toast.success("You're now on your new plan.");
            return;
          }
        } catch {
          // keep polling
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      // Webhook hasn't landed yet — reassure rather than mislead.
      toast.default('Payment received — your plan will update shortly.');
      await loadData(hid);
    },
    [loadData],
  );

  // Stripe redirect handler. The checkout success/cancel URLs now return
  // to /settings/plan directly (the old /billing redirect stripped the
  // query, so this never fired before).
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    if (success === 'true') {
      setSearchParams({}, { replace: true });
      if (householdId) void pollForActivation(householdId);
    } else if (canceled === 'true') {
      toast.default('Checkout canceled');
      setSearchParams({}, { replace: true });
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coming back to this tab after a Stripe hand-off.
  //
  // Checkout now opens in its own tab, so this document is never unloaded:
  // nothing clears the CTA spinner and nothing re-reads the plan, and since
  // `actingTier` also disables every OTHER plan CTA the page would sit
  // permanently inert. When the popup was refused and we redirected in
  // place instead, Safari restores this document from the back/forward
  // cache with its React state frozen mid-flight — same inert page.
  //
  // `pageshow` is what catches the bfcache restore; Safari does not
  // reliably fire focus/visibilitychange for it. All three are wired
  // because which one arrives depends on the browser and the exit route
  // (closed the Stripe tab, switched tabs, or pressed Back).
  useEffect(() => {
    const handleReturn = () => {
      if (!handedOffToStripe.current) return;
      if (document.visibilityState === 'hidden') return;
      setActingTier(null);
      setManaging(false);
      setDowngradeTarget(null);
      if (householdId) void refreshQuietly(householdId);
    };
    window.addEventListener('focus', handleReturn);
    window.addEventListener('pageshow', handleReturn);
    document.addEventListener('visibilitychange', handleReturn);
    return () => {
      window.removeEventListener('focus', handleReturn);
      window.removeEventListener('pageshow', handleReturn);
      document.removeEventListener('visibilitychange', handleReturn);
    };
  }, [householdId, refreshQuietly]);

  const activeTier: SubscriptionTier = useMemo(
    () => subscription?.tier ?? 'FREE',
    [subscription?.tier],
  );
  const isPaid = activeTier !== 'FREE';
  const status = subscription?.status ?? 'ACTIVE';
  const isPastDue = status === 'PAST_DUE';
  const periodEnd = subscription?.current_period_end ?? null;
  const cancelAtPeriodEnd = subscription?.cancel_at_period_end ?? false;

  const quota = TIERS[activeTier].emailQuota;
  const used = reviewed ?? 0;
  const resetLabel = quotaResetLabel(quotaPeriodEnd);
  const statusLine = statusLineFor(used, quota);

  /**
   * Hand off to a Stripe-hosted page.
   *
   * `openExternalTab()` MUST be the first thing that runs and it must run
   * synchronously inside the click handler: minting the session URL is an
   * Edge Function round trip, and iOS Safari revokes the popup allowance
   * the moment we await. Opening after the await is silently blocked.
   *
   * Handing Stripe its own tab is the actual fix for the reported trap —
   * Checkout (and Link's "Confirm it's you" step) is a hosted SPA that owns
   * its own history, so once the current tab is given over to it the way
   * back into Yaruta runs through history we do not control. In a separate
   * tab the app is never lost: closing the Stripe tab is the exit.
   */
  const startStripeHandoff = useCallback(
    (
      createSession: () => Promise<{ url: string }>,
      onFailure: (err: unknown) => void,
    ): Promise<void> => {
      const tab = openExternalTab();
      handedOffToStripe.current = true;
      return (async () => {
        try {
          const { url } = await createSession();
          tab.navigate(url);
          // A new tab leaves this document alive, so release the spinner —
          // otherwise every plan CTA stays disabled behind `actingTier`.
          // When the popup was blocked `navigate` redirected this tab
          // instead, and the pending state should stay put until it goes.
          if (!tab.blocked) {
            setActingTier(null);
            setManaging(false);
          }
        } catch (err) {
          tab.cancel();
          onFailure(err);
        }
      })();
    },
    [],
  );

  const handleUpgrade = useCallback(
    (target: SubscriptionTier) => {
      if (!householdId || actingTier !== null) return;
      const plan = target === 'CORE' ? 'core' : 'family';
      setActingTier(target);
      void startStripeHandoff(
        () =>
          isPaid
            ? // Already paying — Stripe portal handles the plan change.
              billingService.createPortalSession(householdId)
            : billingService.createCheckoutSession(householdId, plan),
        (err) => {
          console.error('Upgrade failed:', err);
          toast.error("Something went wrong. Your plan wasn't changed.");
          setActingTier(null);
        },
      );
    },
    [householdId, isPaid, actingTier, startStripeHandoff],
  );

  // Downgrade / cancel: capture the retention reason (best-effort) then
  // send the admin to the Stripe Customer Portal, which owns the actual
  // change. The webhook flips the tier at period end.
  const handleDowngradeContinue = useCallback(
    (
      reasonKey: string,
      otherText: string | null,
    ): Promise<void> | undefined => {
      if (!householdId || !downgradeTarget) return;
      // Returned (not awaited) so the modal keeps its own submitting state
      // until the hand-off settles. The tab is still opened synchronously
      // inside startStripeHandoff, before any await — the modal calls this
      // in the click task, so the gesture is intact.
      return startStripeHandoff(
        async () => {
          try {
            await billingService.recordDowngradeReason(
              householdId,
              reasonKey,
              otherText,
            );
          } catch (err) {
            console.warn(
              'Failed to record downgrade reason (non-blocking):',
              err,
            );
          }
          return billingService.createPortalSession(householdId);
        },
        (err) => {
          console.error('Failed to open billing portal:', err);
          toast.error('Unable to open billing portal');
        },
      ).then(() => {
        // Either the Portal is opening or it failed and we toasted; the
        // retention modal has served its purpose in both cases.
        setDowngradeTarget(null);
      });
    },
    [householdId, downgradeTarget, startStripeHandoff],
  );

  const handleManage = useCallback(() => {
    if (!householdId || managing) return;
    setManaging(true);
    void startStripeHandoff(
      () => billingService.createPortalSession(householdId),
      (err) => {
        console.error('Portal session failed:', err);
        toast.error('Unable to open billing portal');
        setManaging(false);
      },
    );
  }, [householdId, managing, startStripeHandoff]);

  // Defense-in-depth: restrict to PRIMARY (matches the billing admin gate
  // enforced in the edge functions).
  if (roleResolved && !isPrimary) {
    return (
      <Navigate
        to="/settings"
        replace
      />
    );
  }

  const renderCard = (tier: SubscriptionTier) => {
    const state: PlanCardState = activeTier === tier ? 'active' : 'inactive';
    const cta = state === 'inactive' ? ctaForTarget(activeTier, tier) : null;
    const onCtaClick = cta
      ? cta.isUpgrade
        ? () => handleUpgrade(tier)
        : () => setDowngradeTarget(tier)
      : undefined;
    return (
      <PlanCard
        key={tier}
        tier={tier}
        state={state}
        ctaLabel={cta?.label}
        onCtaClick={onCtaClick}
        ctaLoading={actingTier === tier}
        ctaDisabled={actingTier !== null && actingTier !== tier}
      />
    );
  };

  const manageLabel = isPastDue
    ? 'Update payment method'
    : cancelAtPeriodEnd
      ? 'Resume or manage plan'
      : 'Manage billing';

  return (
    <SettingsSubPage
      title="Plan"
      // Names its destination rather than letting SettingsSubPage fall back to
      // `navigate(-1)` (YAR-426) — the same defect YAR-422 fixed on Forwarding,
      // with Stripe in the role Google played there. Checkout and the billing
      // portal both leave a Stripe page in the history stack, so after an
      // upgrade "back" replayed history straight into Stripe instead of
      // returning to Settings. This page is only ever reached from Settings.
      onBack={() => navigate('/settings')}
    >
      {loading ? (
        <PlanSkeleton />
      ) : loadError ? (
        <div className="flex items-center justify-center py-10">
          <ErrorState
            message="Unable to load your plan"
            onRetry={() => householdId && loadData(householdId)}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-10 px-5 pb-10 pt-5">
          {/* Your plan + usage meter */}
          <section className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Your plan
              </p>
              <h2 className="text-[32px] font-bold leading-10 text-text-headings">
                {TIERS[activeTier].label}
              </h2>
              <p className="text-base text-muted-foreground">
                {quota === null
                  ? 'Unlimited emails confirmed per month'
                  : `Up to ${quota} emails confirmed per month`}
              </p>
            </div>
            <UsageMeter
              used={used}
              quota={quota}
              resetLabel={resetLabel}
              statusLine={statusLine}
            />

            {isPaid && isPastDue && (
              <div className="flex items-start gap-2 rounded-xl border border-warning-border bg-warning-light px-4 py-3">
                <AlertTriangle
                  size={20}
                  className="mt-0.5 shrink-0 text-warning"
                  aria-hidden="true"
                />
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-medium text-foreground">
                    Payment failed
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Update your payment method to keep your plan active.
                  </p>
                </div>
              </div>
            )}

            {/* Only a cancellation gets its own line. A "Renews on …" line
                repeated the date the meter already shows as "Resets …" — on a
                paid plan the two are the same day (YAR-500). */}
            {isPaid && !isPastDue && periodEnd && cancelAtPeriodEnd && (
              <p className="text-sm text-muted-foreground">
                {`Your plan cancels on ${formatDate(periodEnd)}.`}
              </p>
            )}

            {isPaid && (
              <Button
                variant="outline"
                fullWidth
                onClick={handleManage}
                loading={managing}
              >
                {manageLabel}
              </Button>
            )}
          </section>

          {/* Plan comparison */}
          <section className="flex flex-col gap-4">
            <p className="text-base font-medium text-text-heading">Plans</p>
            <div className="flex flex-col gap-4">
              {(['FREE', 'CORE', 'FAMILY'] as const).map((tier) =>
                renderCard(tier),
              )}
            </div>
          </section>

          {/* Inset to the plan cards' content edge rather than their border
              (QA on YAR-230, Oct 5: the sentence ran wider than the cards'
              text). */}
          <div className="flex flex-col gap-2 px-4">
            <p className="text-center text-sm text-muted-foreground">
              Recurring billing is handled by Stripe. Cancel anytime.
            </p>
            {/* Required beside a paid plan CTA, and specified on the Figma
                Plan frame — the screen shipped without them (YAR-230).
                The links are a shade darker than the sentence above them:
                #454545 (`text-neutral-hover`), not the #5e5e5e the
                surrounding paragraph inherits. */}
            <p className="text-center text-sm text-muted-foreground">
              <button
                type="button"
                onClick={() => setLegalSheet('terms')}
                className="text-text-neutral-hover underline underline-offset-2 cursor-pointer hover:text-foreground transition-colors duration-150"
              >
                Terms of Service
              </button>
              {' & '}
              <button
                type="button"
                onClick={() => setLegalSheet('privacy')}
                className="text-text-neutral-hover underline underline-offset-2 cursor-pointer hover:text-foreground transition-colors duration-150"
              >
                Privacy Policy
              </button>
            </p>
          </div>
        </div>
      )}

      {legalSheet === 'terms' && (
        <FullScreenSheet
          title="Terms of Service"
          onClose={() => setLegalSheet(null)}
        >
          <TermsOfServiceBody />
        </FullScreenSheet>
      )}
      {legalSheet === 'privacy' && (
        <FullScreenSheet
          title="Privacy Policy"
          onClose={() => setLegalSheet(null)}
        >
          <PrivacyPolicyBody />
        </FullScreenSheet>
      )}

      <DowngradeReasonModal
        open={downgradeTarget !== null}
        currentTier={activeTier}
        targetTier={downgradeTarget}
        periodEnd={subscription?.current_period_end ?? null}
        onClose={() => setDowngradeTarget(null)}
        onContinue={handleDowngradeContinue}
      />
    </SettingsSubPage>
  );
}
